import React from 'react';

import {assertAndContinue} from 'Asserts';
import ModelTypeConstants, {StorableItemType} from 'constants/ModelTypeConstants';
import StorableChip from 'inventory/chips/StorableChip';
import {PotentialChildStorable} from 'inventory/location-validation/ParentStorableValidationUtils';
import {TypedValue} from 'notebook/tables/TableConstants';
import tableMultiKeyDownHandler from 'notebook/tables/tableMultiKeyDownHandler';
import {StorableInfo, StorablesTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import LocationSearchDropdownContainer from 'samples/storables/LocationSearchDropdown/LocationSearchDropdownContainer';
import {ParentStorableType} from 'samples/storables/StorableSchemaConstants';
import ControlledSearchInput from 'schemas/tags/ControlledSearchInput';
import {ChipSize} from 'ui-library/chips/Chip';
import {ElementKeyboardEvent} from 'util/Types';

interface LocationSelectorInputProps {
  onSetData: (value: string, typedValue: TypedValue | null) => void;
  onEscape: (e: ElementKeyboardEvent) => void;
  onTabKey: (e: ElementKeyboardEvent) => void;
  onUpdateTypedValue: (typedValue: TypedValue | null) => void;
  onUpdateQuery: (query: string) => void;
  onBlur?: () => void;
  query: string;
  typedValue: TypedValue | null;
  registryId: string;
  storableSchemaId?: string;
  autoFocus?: boolean;
  // This will typically be rendered inside a table cell. If that's not the case, set this to true
  isOutsideTable?: boolean;
  includeArchived: boolean;
  parentStorableType: ParentStorableType;
  /** List of child storables that will be moved into the selected location. If provided, will result in
   * rendering available space information & deprioritization. Schemas included to support checking a
   * location's allowed schemas, but if missing simply the number of items will be considered. */
  storablesToBeMoved?: Array<PotentialChildStorable> | null;
}

class LocationSelectorInput extends React.Component<LocationSelectorInputProps> {
  private handleKeyDown = (e: ElementKeyboardEvent): void => {
    tableMultiKeyDownHandler({
      event: e,
      value: this.props.typedValue,
      onSetValue: this.handleSetValue,
      onEscape: this.props.onEscape,
      onTabKey: this.props.onTabKey,
    });
  };

  private handleSetValue = (typedValue: TypedValue | null): void => {
    this.props.onSetData(this.props.query, typedValue);
  };

  private canSelectAdditional = (): boolean => this.getSummaries(this.props.typedValue).length === 0;

  private getLinksAreDisplayed = (): boolean => this.getSummaries(this.props.typedValue).length > 0;

  private removeLink = (): void => {
    this.props.onUpdateTypedValue(null);
  };

  private getSummaries = (typedValue: TypedValue | null): Array<StorableInfo> =>
    (typedValue as StorablesTypedValue | null)?.storableSummaries ?? [];

  private handleUpdateInputValue = (query: string): void => {
    this.props.onUpdateQuery(query);
  };

  private renderLinks = (): JSX.Element | null => {
    const summaries = this.getSummaries(this.props.typedValue);
    if (summaries.length > 0) {
      const summary = summaries[0]; // We should only have one storable at max.
      return (
        <StorableChip
          key={summary.id}
          id={summary.id}
          size={ChipSize.TABLE_CELL}
          onRemove={(e) => {
            this.removeLink();
          }}
        />
      );
    } else {
      return null;
    }
  };

  private getParentStorableTypes = (parentStorableType: ParentStorableType): Array<StorableItemType> => {
    switch (parentStorableType) {
      case ParentStorableType.GRID:
        return [ModelTypeConstants.BOX, ModelTypeConstants.PLATE];
      case ParentStorableType.BOX:
        return [ModelTypeConstants.BOX];
      case ParentStorableType.PLATE:
        return [ModelTypeConstants.PLATE];
      case ParentStorableType.LOCATION:
        return [ModelTypeConstants.LOCATION];
      case ParentStorableType.GRID_OR_LOCATION:
        return [ModelTypeConstants.BOX, ModelTypeConstants.PLATE, ModelTypeConstants.LOCATION];
      case ParentStorableType.MATRIX_PLATE:
        return [ModelTypeConstants.MATRIX_PLATE];
      default:
        assertAndContinue(false, `Unexpected parent storable type ${parentStorableType}`);
        return [];
    }
  };

  private renderSearchDropdown = (ref: React.RefObject<LocationSearchDropdownContainer>): JSX.Element => (
    <LocationSearchDropdownContainer
      ref={ref}
      registryId={this.props.registryId}
      query={this.props.query}
      storableSchemaId={this.props.storableSchemaId}
      storableTypes={this.getParentStorableTypes(this.props.parentStorableType)}
      onSetData={this.handleSetValue}
      includeArchived={this.props.includeArchived}
      storablesToBeMoved={this.props.storablesToBeMoved}
    />
  );

  render(): JSX.Element {
    return (
      <ControlledSearchInput
        shouldForwardKeydown={true}
        canSelectAdditional={this.canSelectAdditional()}
        hasTypedValue={this.getLinksAreDisplayed()}
        onRemoveLastValue={() => {
          this.removeLink();
        }}
        autoFocus={this.props.autoFocus ?? true}
        inputValue={this.props.query}
        onUpdateInputValue={this.handleUpdateInputValue}
        onKeyDown={this.handleKeyDown}
        onBlur={this.props.onBlur}
        renderSelectedValues={this.renderLinks}
        renderSearchDropdown={this.renderSearchDropdown}
        shouldCloseOnInsideClick={false}
        placeholder={this.canSelectAdditional() ? 'Type to search...' : ''}
        isOutsideTable={this.props.isOutsideTable}
      />
    );
  }
}

export default LocationSelectorInput;
